import { CopyableText } from "components/CopyableText/CopyableText";
import { SUPPORT_EMAIL, SUPPORT_PHONE_NUMBER } from "constants/base";
import { ReactNode } from "react";

export const BANK_BRAND_NAME = {
  BREX: "Brex",
  MERCURY: "Mercury",
  CHASE: "Chase",
  SVB: "Silicon Valley Bank - SVB Online Banking",
  STRIPE: "Stripe",
  GUSTO: "Gusto",
  RIPPLING: "Rippling",
  ARC: "Arc",
  DEEL: "Deel",
  JUSTWORKS: "JustWorks",
  RHO: "Rho",
  AMEX: "American Express",
  PAYPAL: "PayPal",
  WISE: "Wise",
  REVOLUT: "Revolut",
  RAMP: "Ramp",
} as const;

const SupportEmail = () => <CopyableText value={SUPPORT_EMAIL} />;
const SupportPhoneNumber = () => <CopyableText value={SUPPORT_PHONE_NUMBER} />;

export const BANK_ACCOUNTANT_ACCESS_STEPS: Record<string, ReactNode[]> = {
  [BANK_BRAND_NAME.BREX]: [
    "Under Team, click Invite user.",
    "Click on Invite an Accounting Firm and search for Inkle.",
    "After clicking Send invite, we will receive an email invitation.",
  ],
  [BANK_BRAND_NAME.MERCURY]: [
    'Click the "Team" tab in your Settings page (app.mercury.com/settings/team)',
    'Click the "Invite" button',
    <>
      Enter the name as Inkle Support - <SupportEmail /> and select the
      bookkeeper role
    </>,
    "Give read-only access without any card",
  ],
  [BANK_BRAND_NAME.CHASE]: [
    'Click on "Account Management" and then "Access & Security Manager".',
    <>
      Enter Inkle Support as the first and last name and enter <SupportEmail />{" "}
      as the email.
    </>,
    "For Username, enter inkle<your_company_name>123 and set a temporary password.",
    'Once the invite has been sent, continue by selecting the "Access level" as "see activity only".',
    'Select "See statements and Documents" from the General Services.',
    <>
      Let the Inkle Team know the username and temporary password on Inkle Chat
      or on <SupportEmail />
    </>,
    <>
      Use the following phone number - <SupportPhoneNumber />
    </>,
  ],
  [BANK_BRAND_NAME.SVB]: [
    'Click on "Admin" on the left menu of your SVB dashboard.',
    <>
      Click on "Add User" and enter Inkle Support - <SupportEmail /> and add
      phone <SupportPhoneNumber />.
    </>,
    'Please do not select the "Primary Contact" checkbox.',
    'Please add the username as "inklesupport".',
    'Select User Role as "View Only" and click on "Submit".',
  ],
  [BANK_BRAND_NAME.STRIPE]: [
    "On the Dashboard, click on the gear icon in the upper-right-hand corner > under Business Settings, Teams & Security click on Team Settings.",
    "Click + New Member in the top right.",
    <>
      Enter the following email address: <SupportEmail />
    </>,
    "Choose the appropriate role (View Only) to set permissions.",
    "Click Invite to send the invitation email.",
  ],
  [BANK_BRAND_NAME.GUSTO]: [
    "Log in to Gusto as the primary admin.",
    "Go to Settings > Permissions.",
    "In the Gusto Admins card, click Add or Edit admins.",
    "Click Add new admin.",
    'Select Not an employee for employment status and Accountant under "Who are they?".',
    'Choose Accounting/Bookkeeping for "What will they help you with?".',
    <>
      Enter <SupportEmail /> as the email and Inkle Support as the name.
    </>,
    "Click Add Admin.",
  ],
  [BANK_BRAND_NAME.RIPPLING]: [
    "Log in to Rippling and go to Company Settings.",
    "Click on the Admins tab.",
    "Select Add an Administrator.",
    "Choose Add an external third party and select Accountant as the admin type.",
    <>
      Enter Inkle Support as the name and <SupportEmail /> for the email.
    </>,
    "We will receive an invitation to complete the connection.",
  ],
  [BANK_BRAND_NAME.ARC]: [
    "Log in to My ARC Portal",
    "Go to User Management or account settings.",
    "Click Add User or Invite User.",
    <>
      Enter Inkle Support as the name and <SupportEmail /> for email.
    </>,
    'Assign the appropriate permissions ("Read-Only").',
    "Confirm the changes, and the accountant will receive an invitation.",
  ],
  [BANK_BRAND_NAME.DEEL]: [
    "Go to Organization Settings > Access Management.",
    "Select the Admins tab.",
    "Click Invite Admin to add or edit an admin.",
    "Choose a role (e.g., bookkeeper).",
    <>
      Enter Inkle Support as the name and <SupportEmail /> for email.
    </>,
  ],
  [BANK_BRAND_NAME.JUSTWORKS]: [
    "Login to JustWorks",
    "Navigate to the people section available on the left sidebar",
    'Click on Third party Admins > Click on "Add firm"',
    <>
      Enter Inkle Support as the name and <SupportEmail /> for email and click
      on continue.
    </>,
    "Set the permissions > Share the invite",
  ],
  [BANK_BRAND_NAME.RHO]: [
    "Go to User Management > Find Users",
    "Select the Invite Users option",
    <>
      Enter Inkle Support as the name and <SupportEmail /> for email & assign
      role to the user (user group - Book-keeper).
    </>,
    "Click on Invite User to share the invitation.",
  ],
  [BANK_BRAND_NAME.AMEX]: [
    "Login & Access - Account services",
    "Click Add or Remove account manager",
    "Click Add account manager > Click on Continue",
    "Send yourself a code to submit the change > click continue",
    "Enter OTP & Click Continue",
    "Select the card you want to provide access to & Click Continue",
    <>
      Enter Inkle Support as the name and <SupportEmail /> for email {">"} Click
      Continue {">"} Limited Access {">"} Click Continue
    </>,
    "Check the necessary boxes > Click Submit >Click Done",
  ],
  [BANK_BRAND_NAME.PAYPAL]: [
    "Go to Settings on top-right",
    "Click on Manage Users > Add User",
    <>
      Choose a Username "inkle" and add <SupportEmail /> as email
    </>,
    <>
      Use <SupportPhoneNumber /> as phone number if asked
    </>,
    'Give "Read only/view only" access and submit.',
  ],
  [BANK_BRAND_NAME.WISE]: [
    "Log in to your Wise Business Account.",
    "Select your business name in the top-right corner.",
    "Go to Team members and payment approvals.",
    <>
      Select Add team member and enter their email address (<SupportEmail />)
    </>,
    "Set permissions: Choose what they can view (e.g., view only, create payments, or manage integrations).",
    "Confirm the invite.",
  ],
  [BANK_BRAND_NAME.REVOLUT]: [
    "Log in to your Revolut Business account.",
    "Go to the 'Team' section.",
    <>
      Click 'Invite' and enter the accountant's email address (<SupportEmail />)
    </>,
    "Select the appropriate role as Accountant",
    "Click 'Continue' to send the invitation.",
  ],
  [BANK_BRAND_NAME.RAMP]: [
    "Log in to Ramp: Access your company dashboard as an admin.",
    "Navigate to People: Click on the People tab in the main menu.",
    "Invite Accounting Firm: Go to the Accounting firms tab and click Invite Accounting Firm.",
    <>
      Input your accountant's email address (<SupportEmail />) and select the
      appropriate roles (e.g., Bookkeeper).
    </>,
    "Send Invitation: Click Send Invite. The accountant will receive an email to set up their access.",
  ],
};

export const DEFAULT_BANK_ACCOUNTANT_ACCESS_STEPS: ReactNode[] = [
  "Log in to your bank's dashboard.",
  <>
    Add Inkle Support (<SupportEmail />) as an accountant with read-only access.
  </>,
  "Confirm below once access has been granted.",
];
